import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { getAllDivisionNames } from '../utils/divisionUtils';
import { divisionsAPI } from '../services/api';
import TeamLogoModal from './TeamLogoModal';
import { getPromotionRelegationIconsForLeaderboardRow } from '../utils/promotionRelegationEmojis';

// Swiss Cantons Mapping - Abbreviations to Full Names
const CANTON_NAMES = {
  'AG': 'Aargau',
  'AI': 'Appenzell Innerrhoden',
  'AR': 'Appenzell Ausserrhoden',
  'BE': 'Bern',
  'BL': 'Basel-Landschaft',
  'BS': 'Basel-Stadt',
  'FR': 'Fribourg',
  'GE': 'Geneva',
  'GL': 'Glarus',
  'GR': 'Grisons',
  'JU': 'Jura',
  'LU': 'Lucerne',
  'NE': 'Neuchâtel',
  'NW': 'Nidwalden',
  'OW': 'Obwalden',
  'SG': 'St. Gallen',
  'SH': 'Schaffhausen',
  'SO': 'Solothurn',
  'SZ': 'Schwyz',
  'TG': 'Thurgau',
  'TI': 'Ticino',
  'UR': 'Uri',
  'VD': 'Vaud',
  'VS': 'Valais',
  'ZG': 'Zug',
  'ZH': 'Zurich'
};

// Swiss Cantons Data - Updated with new consolidated cantons
// eslint-disable-next-line no-unused-vars
const SWISS_CANTONS = [
  'Zurich', 'Bern', 'Lucerne', 'Schwyz', 'Glarus', 'Zug', 'Fribourg',
  'Solothurn', 'Schaffhausen', 'St. Gallen', 'Aargau', 'Thurgau', 'Ticino', 'Vaud', 'Valais', 'Neuchâtel', 'Geneva', 'Jura',
  // New consolidated cantons
  'Appenzell', 'Basel', 'Nidwalden', 'Grisons'
];

// eslint-disable-next-line no-unused-vars
const DIVISIONS = getAllDivisionNames();

// Generate consistent mock team data (8 teams for optimal division)
// eslint-disable-next-line no-unused-vars
const generateMockTeams = (canton, division) => {
  const teams = [];
  
  // Generate 8 teams for optimal division display
  for (let i = 1; i <= 8; i++) {
    teams.push({
      id: i,
      name: `${canton} ${division} Team ${i}`,
      points: Math.max(1, 20 - i * 2), // Consistent point distribution
      games: 6, // All teams have same number of games
      wins: Math.max(1, 6 - i + 1), // Consistent win distribution
      losses: i - 1 // Consistent loss distribution
    });
  }
  
  return teams;
};

// Helper function to clean and format division name by removing canton prefixes and fixing capitalization
const cleanDivisionName = (name, cantonCode) => {
  if (!name) return '';
  
  // Get full canton name if code exists
  const fullCantonName = CANTON_NAMES[cantonCode];
  
  // Create patterns to match at the start of the name
  const patterns = [
    new RegExp(`^${cantonCode}\\s+`, 'i'), // "AG " at start
    new RegExp(`^${fullCantonName}\\s+`, 'i'), // "Aargau " at start
    new RegExp(`^${cantonCode}`, 'i'), // "AG" at start
    new RegExp(`^${fullCantonName}`, 'i'), // "Aargau" at start
  ];
  
  let cleaned = name;
  for (const pattern of patterns) {
    if (pattern.test(cleaned)) {
      cleaned = cleaned.replace(pattern, '').trim();
    }
  }
  
  // Fix common typos and capitalization issues
  // Fix "ginner" -> "Beginner", "biggner" -> "Beginner", etc.
  cleaned = cleaned.replace(/\bginner\b/gi, 'Beginner');
  cleaned = cleaned.replace(/\bbiggner\b/gi, 'Beginner');
  cleaned = cleaned.replace(/\bbegginer\b/gi, 'Beginner');
  cleaned = cleaned.replace(/\bbegginner\b/gi, 'Beginner');
  
  // Properly capitalize division names
  // Capitalize first letter of each word, but keep "Group A", "Group B", etc. as is
  const words = cleaned.split(/\s+/);
  const capitalized = words.map((word, index) => {
    // Keep "Group" capitalized, and keep group letters (A, B, C) uppercase
    if (word.toLowerCase() === 'group' && index < words.length - 1) {
      return 'Group';
    }
    // Capitalize first letter, lowercase the rest
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
  
  return capitalized.join(' ');
};

// Transform API data to slider shape
const mapApiDivisionToSlide = (division) => {
  const mapped = {
    id: division.id,
    canton: division.canton,
    division: cleanDivisionName(division.name, division.canton),
    teams: (division.teams || []).slice(0, 8).map((t, idx) => {
      const teamData = {
        id: t.id,
        name: t.team_name || t.name,
        logo: t.team_logo_url || null,
        points: typeof t.points === 'number' ? t.points : Math.max(1, 18 - idx * 2),
        games: typeof t.games === 'number' ? t.games : 0,
        wins: typeof t.wins === 'number' ? t.wins : 0,
        losses: typeof t.losses === 'number' ? t.losses : 0,
        promotion_status: t.promotion_status || null,
        promotion_completed_season_name: t.promotion_completed_season_name || null,
      };
      
      // Debug logging for teams with promotion status
      if (teamData.promotion_status) {
        console.log(`🏠 [HOME LEADERBOARD] Team ${teamData.name} (${teamData.id}) has promotion_status: ${teamData.promotion_status}`);
      }
      
      return teamData;
    }),
  };
  
  return mapped;
};

// Styled Components
const SliderContainer = styled.div`
  position: relative;
  background-color: #f8fafc;
  padding: 3rem 0;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SliderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
`;

const SliderTitle = styled.h2`
  color: #1f2937;
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
`;

const SliderNavigation = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const NavButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid #043663;
  background: ${props => props.disabled ? '#f3f4f6' : '#ffffff'};
  color: ${props => props.disabled ? '#9ca3af' : '#043663'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  font-size: 1.2rem;
  
  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    font-size: 1.1rem;
  }
  
  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
  
  &:hover:not(:disabled) {
    background: #043663;
    color: #ffffff;
    transform: scale(1.1);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
`;

const SlideWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 640px; /* Increased to accommodate 8 teams */
  overflow: hidden;
  border-radius: 16px;
  
  @media (max-width: 768px) {
    height: 600px; /* Increased to accommodate 8 teams on tablet */
    border-radius: 12px;
  }
  
  @media (max-width: 480px) {
    height: 560px; /* Increased to accommodate 8 teams on mobile */
    border-radius: 8px;
  }
`;

const SlideTrack = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  transition: opacity 0.3s ease;
`;

const Slide = styled.div`
  width: 100%;
  height: 100%;
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  opacity: ${props => props.$isTransitioning ? 0.7 : 1};
  transition: opacity 0.3s ease;
  
  @media (max-width: 768px) {
    border-radius: 12px;
  }
  
  @media (max-width: 480px) {
    border-radius: 8px;
  }
`;

const DivisionInfo = styled.div`
  background: linear-gradient(135deg, #043663 0%, #0a4d7a 100%);
  color: white;
  padding: 1.5rem 2rem;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.8rem 1rem;
  }
  
  h3 {
    margin: 0 0 0.5rem 0;
    font-size: 1.5rem;
    font-weight: 700;
    
    @media (max-width: 768px) {
      font-size: 1.3rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1.1rem;
    }
  }
  
  p {
    margin: 0;
    opacity: 0.9;
    font-size: 1rem;
    
    @media (max-width: 768px) {
      font-size: 0.9rem;
    }
    
    @media (max-width: 480px) {
      font-size: 0.8rem;
    }
  }
`;

const LeaderboardTable = styled.div`
  background-color: #ffffff;
  border-radius: 0 0 16px 16px;
  overflow: hidden;
  height: calc(100% - 80px);
  overflow-y: auto;
  
  /* Ensure all 8 teams are visible without scroll */
  @media (min-width: 769px) {
    height: 560px; /* Fixed height to fit all 8 teams */
    overflow-y: visible; /* Remove scroll on desktop */
  }
  
  @media (max-width: 768px) {
    height: 520px; /* Fixed height to fit all 8 teams on tablet */
    overflow-y: visible; /* Remove scroll on tablet */
  }
  
  @media (max-width: 480px) {
    height: 480px; /* Fixed height to fit all 8 teams on mobile */
    overflow-y: visible; /* Remove scroll on mobile */
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr 80px 80px 80px;
  padding: 16px 12px;
  background-color: #043663;
  color: white;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 1;
  font-size: 0.9rem;
  
  span {
    text-align: center;
  }

  span:nth-child(2) {
    text-align: left;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 50px 1fr 60px 60px 60px;
    padding: 12px 8px;
    font-size: 0.8rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 40px 1fr 50px 50px 50px;
    padding: 10px 6px;
    font-size: 0.75rem;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr 80px 80px 80px;
  padding: 16px 12px;
  border-bottom: 1px solid #e5e7eb;
  align-items: center;
  font-size: 0.9rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 50px 1fr 60px 60px 60px;
    padding: 12px 8px;
    font-size: 0.8rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 40px 1fr 50px 50px 50px;
    padding: 10px 6px;
    font-size: 0.75rem;
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  &:nth-child(even) {
    background-color: #f8fafc;
  }
  
  .rank {
    font-weight: 700;
    color: #043663;
    text-align: center;
  }
  
  .team {
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding-right: 8px;
  }
  
  .points {
    font-weight: 600;
    color: #16a34a;
    text-align: center;
  }
  
  .games, .record {
    text-align: center;
    font-weight: 500;
  }
`;

const TeamCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  overflow: hidden;
`;

const TeamLogo = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
`;

const TeamInitials = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e5f0fa;
  color: #043663;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.8rem;
  border: 1px solid #cfe0f5;
`;

const SliderDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 2rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 6px;
    margin-top: 1.5rem;
  }
  
  @media (max-width: 480px) {
    gap: 4px;
    margin-top: 1rem;
  }
`;

const Dot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: ${props => props.$active ? '#043663' : '#d1d5db'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    width: 10px;
    height: 10px;
  }
  
  @media (max-width: 480px) {
    width: 8px;
    height: 8px;
  }
  
  &:hover {
    background: #043663;
    transform: scale(1.2);
  }
  
  &:active {
    transform: scale(0.9);
  }
`;

const LeaderboardActions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 12px;
    margin-top: 1.5rem;
  }
  
  @media (max-width: 480px) {
    gap: 10px;
    margin-top: 1rem;
  }
`;

const Button = styled(Link).withConfig({
  shouldForwardProp: (prop) => !['$primary'].includes(prop),
})`
  display: inline-block;
  padding: 12px 24px;
  border: 2px solid #043663;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  text-decoration: none;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
  background: ${props => props.$primary ? 'linear-gradient(135deg, #043663 0%, #0a4d7a 100%)' : 'transparent'};
  color: ${props => props.$primary ? '#ffffff' : '#043663'};
  min-width: 140px;
  
  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 0.9rem;
    min-width: 120px;
  }
  
  @media (max-width: 480px) {
    padding: 8px 16px;
    font-size: 0.85rem;
    min-width: 100px;
  }
  
  &:hover {
    background: ${props => props.$primary ? 'linear-gradient(135deg, #032a4a 0%, #043663 100%)' : 'linear-gradient(135deg, #043663 0%, #0a4d7a 100%)'};
    color: #ffffff;
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// eslint-disable-next-line no-unused-vars
const ComingSoonOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(4, 54, 99, 0.95);
  color: white;
  padding: 2rem 2.5rem;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.4);
  border: 2px solid rgba(255, 255, 255, 0.3);
  z-index: 10;
  backdrop-filter: blur(5px);
  max-width: 90%;
  width: auto;
  min-width: 280px;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1.8rem;
    border-radius: 10px;
    max-width: 95%;
    min-width: 260px;
  }
  
  @media (max-width: 480px) {
    padding: 1.2rem 1.5rem;
    border-radius: 8px;
    max-width: 98%;
    min-width: 240px;
  }
`;

// eslint-disable-next-line no-unused-vars
const ComingSoonIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 0.8rem;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
    margin-bottom: 0.6rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
    margin-bottom: 0.5rem;
  }
`;

// eslint-disable-next-line no-unused-vars
const ComingSoonTitle = styled.h4`
  margin: 0 0 0.8rem 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: white;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 0.6rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }
`;

// eslint-disable-next-line no-unused-vars
const ComingSoonText = styled.p`
  margin: 0;
  font-size: 1rem;
  opacity: 1;
  color: white;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

// Main Component
const LeaderboardSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [allDivisions, setAllDivisions] = useState([]);
  const [cantonGroups, setCantonGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  // Team logo modal state
  const [teamLogoModalOpen, setTeamLogoModalOpen] = useState(false);
  const [selectedTeamLogo, setSelectedTeamLogo] = useState(null);
  const [selectedTeamName, setSelectedTeamName] = useState(null);
  const [selectedTeamInitials, setSelectedTeamInitials] = useState(null);

  const [gapPeriodMessage, setGapPeriodMessage] = useState(null);
  const [upcomingSeason, setUpcomingSeason] = useState(null);
  const [leaderboardSeasonName, setLeaderboardSeasonName] = useState(null);

  // Load live leaderboard divisions
  useEffect(() => {
    const loadDivisions = async () => {
      setIsLoading(true);
      try {
        const res = await divisionsAPI.getPublicLeaderboard({ min_teams: 1 });
        const data = res?.data || res; // interceptor returns data
        
        // Check if we're in gap period
        if (data.isGapPeriod && data.message) {
          setGapPeriodMessage(data.message);
          setUpcomingSeason(data.upcomingSeason || null);
          setLeaderboardSeasonName(null);
          setAllDivisions([]);
          setCantonGroups([]);
          setIsLoading(false);
          return;
        }

        setLeaderboardSeasonName(res?.season?.name ?? null);
        
        const divisions = (data || []).map(mapApiDivisionToSlide);
        
        // Group divisions by canton
        const groupedByCanton = {};
        divisions.forEach(division => {
          if (!groupedByCanton[division.canton]) {
            groupedByCanton[division.canton] = [];
          }
          groupedByCanton[division.canton].push(division);
        });

        // Sort divisions within each canton: first by skill level, then by group letter (A, B, C)
        Object.keys(groupedByCanton).forEach(canton => {
          groupedByCanton[canton].sort((a, b) => {
            // Extract skill level from division name (e.g., "Intermediate", "Beginner", "Advanced")
            const getSkillLevel = (name) => {
              const nameLower = name.toLowerCase();
              // Order: Elite > Advanced + > Advanced > Intermediate + > Intermediate > Beginner + > Beginner
              if (nameLower.includes('elite')) return 0;
              if (nameLower.includes('advanced +') || nameLower.includes('confirmed advanced')) return 1;
              if (nameLower.includes('advanced')) return 2;
              if (nameLower.includes('intermediate +') || nameLower.includes('confirmed intermediate')) return 3;
              if (nameLower.includes('intermediate')) return 4;
              if (nameLower.includes('beginner +') || nameLower.includes('confirmed beginner')) return 5;
              if (nameLower.includes('beginner')) return 6;
              return 99; // Unknown skill level goes last
            };
            
            // Extract group letter from division name (e.g., "Group A" -> "A")
            const getGroupLetter = (name) => {
              const match = name.match(/\bGroup\s+([A-Z])\b/i);
              return match ? match[1].toUpperCase() : null;
            };
            
            const skillLevelA = getSkillLevel(a.division);
            const skillLevelB = getSkillLevel(b.division);
            
            // First, sort by skill level (lower number = higher skill)
            if (skillLevelA !== skillLevelB) {
              return skillLevelA - skillLevelB;
            }
            
            // If same skill level, sort by group letter (A, B, C)
            const groupA = getGroupLetter(a.division);
            const groupB = getGroupLetter(b.division);
            
            if (groupA && groupB) {
              return groupA.localeCompare(groupB);
            }
            
            // If only one has a group letter, put it first
            if (groupA && !groupB) return -1;
            if (!groupA && groupB) return 1;
            
            // Otherwise, sort alphabetically by division name
            return a.division.localeCompare(b.division);
          });
        });

        // Create canton groups with divisions that have teams
        const cantonGroups = Object.entries(groupedByCanton).map(([canton, divisions]) => ({
          canton,
          divisions: divisions.filter(div => div.teams && div.teams.length > 0),
          hasTeams: divisions.some(div => div.teams && div.teams.length > 0)
        }));

        // Only show cantons that have teams
        const activeCantonGroups = cantonGroups.filter(group => group.hasTeams);
        
        // Sort all divisions: first by canton, then by skill level, then by group letter (A, B, C)
        const sortedDivisions = divisions
          .filter(div => div.teams && div.teams.length > 0)
          .sort((a, b) => {
            // First sort by canton
            if (a.canton !== b.canton) {
              return a.canton.localeCompare(b.canton);
            }
            
            // Extract skill level from division name (e.g., "Intermediate", "Beginner", "Advanced")
            const getSkillLevel = (name) => {
              const nameLower = name.toLowerCase();
              // Order: Elite > Advanced + > Advanced > Intermediate + > Intermediate > Beginner + > Beginner
              if (nameLower.includes('elite')) return 0;
              if (nameLower.includes('advanced +') || nameLower.includes('confirmed advanced')) return 1;
              if (nameLower.includes('advanced')) return 2;
              if (nameLower.includes('intermediate +') || nameLower.includes('confirmed intermediate')) return 3;
              if (nameLower.includes('intermediate')) return 4;
              if (nameLower.includes('beginner +') || nameLower.includes('confirmed beginner')) return 5;
              if (nameLower.includes('beginner')) return 6;
              return 99; // Unknown skill level goes last
            };
            
            // Extract group letter from division name (e.g., "Group A" -> "A")
            const getGroupLetter = (name) => {
              const match = name.match(/\bGroup\s+([A-Z])\b/i);
              return match ? match[1].toUpperCase() : null;
            };
            
            const skillLevelA = getSkillLevel(a.division);
            const skillLevelB = getSkillLevel(b.division);
            
            // Second, sort by skill level (lower number = higher skill)
            if (skillLevelA !== skillLevelB) {
              return skillLevelA - skillLevelB;
            }
            
            // If same skill level, sort by group letter (A, B, C)
            const groupA = getGroupLetter(a.division);
            const groupB = getGroupLetter(b.division);
            
            if (groupA && groupB) {
              return groupA.localeCompare(groupB);
            }
            
            // If only one has a group letter, put it first
            if (groupA && !groupB) return -1;
            if (!groupA && groupB) return 1;
            
            // Otherwise, sort alphabetically by division name
            return a.division.localeCompare(b.division);
          });
        
        setCantonGroups(activeCantonGroups);
        setAllDivisions(sortedDivisions);
        setGapPeriodMessage(null);
        setUpcomingSeason(null);
      } catch (e) {
        console.error('Failed to load public leaderboard:', e);
        setAllDivisions([]);
        setCantonGroups([]);
        setGapPeriodMessage(null);
        setUpcomingSeason(null);
        setLeaderboardSeasonName(null);
      }
      setIsLoading(false);
    };

    loadDivisions();
  }, []);

  const nextSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(prev => (prev + 1) % allDivisions.length);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const prevSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(prev => (prev - 1 + allDivisions.length) % allDivisions.length);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const goToSlide = (index) => {
    if (isTransitioning || index === currentSlide) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  // Touch event handlers for mobile swipe
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentSlide < allDivisions.length - 1) {
      goToSlide(currentSlide + 1);
    }
    if (isRightSwipe && currentSlide > 0) {
      goToSlide(currentSlide - 1);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') prevSlide();
      if (e.key === 'ArrowRight') nextSlide();
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTransitioning]);
  // Note: nextSlide and prevSlide intentionally excluded - they're recreated on each render and would cause infinite loops

  // Live leaderboard code
  if (isLoading) {
    return (
      <SliderContainer>
        <Container>
          <div style={{ textAlign: 'center', padding: '4rem 0' }}>
            <h2>Loading Leaderboards...</h2>
          </div>
        </Container>
      </SliderContainer>
    );
  }

  // If gap period, show graceful message
  if (gapPeriodMessage) {
    return (
      <SliderContainer>
        <Container>
          <div style={{ textAlign: 'center', padding: '4rem 2rem', maxWidth: '800px', margin: '0 auto' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⏳</div>
            <h2 style={{ fontSize: '1.75rem', marginBottom: '1rem', color: '#1f2937' }}>
              New Season Starting Soon
            </h2>
            <p style={{ fontSize: '1.1rem', color: '#6b7280', marginBottom: '2rem', lineHeight: '1.6' }}>
              {gapPeriodMessage}
            </p>
            {upcomingSeason?.startDate && (
              <div style={{ 
                display: 'inline-block', 
                padding: '0.75rem 1.5rem', 
                backgroundColor: '#043663', 
                color: 'white', 
                borderRadius: '0.5rem',
                fontSize: '1rem',
                fontWeight: '600'
              }}>
                Starts: {new Date(upcomingSeason.startDate).toLocaleDateString('en-GB', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </div>
            )}
          </div>
        </Container>
      </SliderContainer>
    );
  }

  // If no cantons have teams, show message
  if (cantonGroups.length === 0) {
    return (
      <SliderContainer>
        <Container>
          <div style={{ textAlign: 'center', padding: '4rem 0' }}>
            <h2>No teams representing any canton at the moment</h2>
            <p style={{ color: '#6b7280', marginTop: '1rem' }}>
              Be the first to register your team and represent your canton!
            </p>
          </div>
        </Container>
      </SliderContainer>
    );
  }

  const currentDivision = allDivisions[currentSlide];

  if (!currentDivision) {
    return (
      <SliderContainer>
        <Container>
          <div style={{ textAlign: 'center', padding: '4rem 0' }}>
            <h2>No leaderboard data available</h2>
          </div>
        </Container>
      </SliderContainer>
    );
  }

  return (
    <SliderContainer>
      <Container>
        <SliderHeader>
          <div>
            <SliderTitle>Leaderboards</SliderTitle>
            <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: '0.5rem 0 0 0' }}>
              {cantonGroups.length} Canton{cantonGroups.length !== 1 ? 's' : ''} Represented
            </p>
          </div>
          <SliderNavigation>
            <NavButton onClick={prevSlide} disabled={isTransitioning}>
              ‹
            </NavButton>
            <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>
              {currentSlide + 1} of {allDivisions.length}
            </span>
            <NavButton onClick={nextSlide} disabled={isTransitioning}>
              ›
            </NavButton>
          </SliderNavigation>
        </SliderHeader>
        
        <SlideWrapper
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <SlideTrack>
            <Slide $isTransitioning={isTransitioning}>
              <DivisionInfo>
                <h3>{CANTON_NAMES[currentDivision?.canton] || currentDivision?.canton} - {currentDivision?.division}</h3>
              </DivisionInfo>
              <LeaderboardTable style={{ 
                position: 'relative'
              }}>
                <TableHeader>
                  <span>Rank</span>
                  <span>Team</span>
                  <span>Games</span>
                  <span>Points</span>
                  <span>W-L</span>
                </TableHeader>
                {(currentDivision?.teams || []).map((team, teamIndex) => {
                  const rowPrIcons = getPromotionRelegationIconsForLeaderboardRow(
                    team.promotion_status,
                    team.promotion_completed_season_name,
                    leaderboardSeasonName
                  );
                  return (
                  <TableRow key={team.id}>
                    <span className="rank">{teamIndex + 1}</span>
                    <TeamCell className="team">
                      {team.logo ? (
                        <TeamLogo 
                          src={team.logo} 
                          alt={`${team.name} logo`} 
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            setSelectedTeamLogo(team.logo);
                            setSelectedTeamName(team.name);
                            setSelectedTeamInitials((team.name || '').split(' ').map(w => w && w[0]).join('').slice(0,2).toUpperCase());
                            setTeamLogoModalOpen(true);
                          }}
                        />
                      ) : (
                        <TeamInitials
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            setSelectedTeamLogo(null);
                            setSelectedTeamName(team.name);
                            setSelectedTeamInitials((team.name || '').split(' ').map(w => w && w[0]).join('').slice(0,2).toUpperCase());
                            setTeamLogoModalOpen(true);
                          }}
                        >
                          {(team.name || '').split(' ').map(w => w && w[0]).join('').slice(0,2).toUpperCase()}
                        </TeamInitials>
                      )}
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {team.name}
                        {(() => {
                          // Debug logging for specific team
                          if (team.name && team.name.toLowerCase().includes('acolyte')) {
                            console.log(`🔍 [HOME LEADERBOARD RENDER] Team "${team.name}" (${team.id}) - promotion_status:`, team.promotion_status, 'Full team object:', team);
                          }
                          return null;
                        })()}
                        {team.promotion_status === 'promote' && (
                          <span style={{ marginLeft: '6px', color: '#16a34a', fontSize: '1.1em' }} title="Promoted from lower division">{rowPrIcons.promote}</span>
                        )}
                        {team.promotion_status === 'relegate' && (
                          <span style={{ marginLeft: '6px', color: '#dc2626', fontSize: '1.1em' }} title="Relegated from higher division">{rowPrIcons.relegate}</span>
                        )}
                      </span>
                    </TeamCell>
                    <span className="games">{team.games}</span>
                    <span className="points">{team.points}</span>
                    <span className="record">{`${team.wins}-${team.losses}`}</span>
                  </TableRow>
                );
                })}
              </LeaderboardTable>
            </Slide>
          </SlideTrack>
        </SlideWrapper>
        
        <SliderDots>
          {allDivisions.map((_, index) => (
            <Dot
              key={index}
              $active={currentSlide === index}
              onClick={() => goToSlide(index)}
            />
          ))}
        </SliderDots>
        
        <LeaderboardActions>
          <Button to="/dashboard">Rankings</Button>
          <Button to="/login">Login</Button>
          <Button $primary to="/register">Register Your Team</Button>
        </LeaderboardActions>
      </Container>
      
      {/* Team Logo Modal */}
      <TeamLogoModal
        isOpen={teamLogoModalOpen}
        onClose={() => setTeamLogoModalOpen(false)}
        logoUrl={selectedTeamLogo}
        teamName={selectedTeamName}
        teamInitials={selectedTeamInitials}
      />
    </SliderContainer>
  );
};

export default LeaderboardSlider;
